import { useCallback, useEffect, useMemo, useState } from 'react';

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultFilters = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 6);
  return {
    startDate: toDateInput(start),
    endDate: toDateInput(today),
    includeArticulos: true,
    includeRecetas: true,
    aggregate: false,
    forecastDetail: false,
    divisionId: '',
    areaId: '',
    restaurantId: ''
  };
};

export const useAdminUsageMetrics = () => {
  const [filters, setFilters] = useState(getDefaultFilters);
  const [draftFilters, setDraftFilters] = useState(getDefaultFilters);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [trends, setTrends] = useState({
    labels: [],
    uniqueUsers: [],
    totalClicks: [],
    divisionBreakdown: [],
    areaBreakdown: [],
    processTypeBreakdown: { articulos: [], recetas: [] },
    startDate: '',
    endDate: ''
  });
  const [catalogs, setCatalogs] = useState({
    divisions: [],
    areas: [],
    restaurants: [],
    loading: false,
    error: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCatalogs = useCallback(async () => {
    setCatalogs((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [divisionsRes, areasRes, restaurantsRes] = await Promise.all([
        fetch('/admin/api/divisions?active_only=1'),
        fetch('/admin/api/areas'),
        fetch('/admin/api/sucursales-dropdown')
      ]);
      const [divisionsData, areasData, restaurantsData] = await Promise.all([
        divisionsRes.json(),
        areasRes.json(),
        restaurantsRes.json()
      ]);
      if (!divisionsRes.ok || divisionsData?.error) {
        throw new Error(divisionsData?.error || 'No se pudieron cargar las divisiones');
      }
      if (!areasRes.ok || areasData?.error) {
        throw new Error(areasData?.error || 'No se pudieron cargar las áreas');
      }
      if (!restaurantsRes.ok || restaurantsData?.error) {
        throw new Error(restaurantsData?.error || 'No se pudieron cargar los restaurantes');
      }
      setCatalogs({
        divisions: divisionsData?.divisions || [],
        areas: areasData?.areas || [],
        restaurants: restaurantsData?.sucursales || [],
        loading: false,
        error: null
      });
    } catch (err) {
      setCatalogs((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No se pudieron cargar los catálogos'
      }));
    }
  }, []);

  const loadMetrics = useCallback(async (override = {}) => {
    const hasOverride = Object.keys(override).length > 0;
    const nextFilters = hasOverride ? { ...draftFilters, ...override } : draftFilters;
    if (!nextFilters.startDate || !nextFilters.endDate) {
      throw new Error('Selecciona un rango de fechas válido.');
    }
    if (nextFilters.endDate < nextFilters.startDate) {
      throw new Error('La fecha fin debe ser posterior o igual a la fecha inicio.');
    }
    if (!nextFilters.includeArticulos && !nextFilters.includeRecetas) {
      throw new Error('Selecciona al menos un tipo de pronóstico.');
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        start_date: nextFilters.startDate,
        end_date: nextFilters.endDate,
        aggregate: nextFilters.aggregate ? '1' : '0',
        include_articulos: nextFilters.includeArticulos ? '1' : '0',
        include_recetas: nextFilters.includeRecetas ? '1' : '0'
      });
      if (nextFilters.divisionId) {
        params.append('division_id', nextFilters.divisionId);
      }
      if (nextFilters.areaId) {
        params.append('area_id', nextFilters.areaId);
      }
      if (nextFilters.restaurantId) {
        params.append('restaurant_id', nextFilters.restaurantId);
      }
      const response = await fetch(`/admin/api/usage?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las métricas');
      }
      if (hasOverride) {
        setDraftFilters(nextFilters);
      }
      setFilters(nextFilters);
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (err) {
      setError(err?.message || 'Error al cargar métricas');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [draftFilters]);

  useEffect(() => {
    loadMetrics().catch(() => {});
  }, []); // carga inicial única para evitar reconsultas en bucle

  useEffect(() => {
    loadCatalogs().catch(() => {});
  }, [loadCatalogs]);

  const loadTrends = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
        include_articulos: filters.includeArticulos ? '1' : '0',
        include_recetas: filters.includeRecetas ? '1' : '0'
      });
      if (filters.divisionId) {
        params.append('division_id', filters.divisionId);
      }
      if (filters.areaId) {
        params.append('area_id', filters.areaId);
      }
      if (filters.restaurantId) {
        params.append('restaurant_id', filters.restaurantId);
      }
      const response = await fetch(`/admin/api/usage/trends?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las tendencias');
      }
      setTrends({
        labels: data.labels || [],
        uniqueUsers: data.unique_users || [],
        totalClicks: data.total_clicks || [],
        divisionBreakdown: data.division_breakdown || [],
        areaBreakdown: data.area_breakdown || [],
        processTypeBreakdown: data.process_type_breakdown || { articulos: [], recetas: [] },
        startDate: data.start_date,
        endDate: data.end_date
      });
    } catch (err) {
      setTrends({ labels: [], uniqueUsers: [], totalClicks: [], divisionBreakdown: [], areaBreakdown: [], processTypeBreakdown: { articulos: [], recetas: [] } });
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.includeArticulos,
    filters.includeRecetas,
    filters.divisionId,
    filters.areaId,
    filters.restaurantId
  ]);

  useEffect(() => {
    loadTrends().catch(() => {});
  }, [loadTrends]);

  const exportMetrics = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error('Selecciona un rango de fechas válido antes de exportar.');
    }
    if (!filters.includeArticulos && !filters.includeRecetas) {
      throw new Error('Selecciona al menos un tipo de pronóstico para exportar.');
    }
    const params = new URLSearchParams({
      start_date: filters.startDate,
      end_date: filters.endDate,
      aggregate: filters.aggregate ? '1' : '0',
      include_articulos: filters.includeArticulos ? '1' : '0',
      include_recetas: filters.includeRecetas ? '1' : '0',
      forecast_detail: filters.forecastDetail ? '1' : '0'
    });
    if (filters.divisionId) {
      params.append('division_id', filters.divisionId);
    }
    if (filters.areaId) {
      params.append('area_id', filters.areaId);
    }
    if (filters.restaurantId) {
      params.append('restaurant_id', filters.restaurantId);
    }
    const response = await fetch(`/admin/api/usage/export?${params.toString()}`);
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'No se pudo exportar el archivo.');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uso_modelo_${filters.startDate}_${filters.endDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [filters]);

  const setFilterValue = useCallback((name, value) => {
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const stats = useMemo(() => ({
    totalClicks: summary?.total_clicks || 0,
    articulos: summary?.articulos || 0,
    recetas: summary?.recetas || 0
  }), [summary]);

  return {
    filters,
    draftFilters,
    rows,
    summary: stats,
    trends,
    catalogs,
    loading,
    error,
    loadMetrics,
    setFilterValue,
    exportMetrics
  };
};
